<script setup>
/**
 * How customers are reacting to one piece of wording — on its own, beside the verdict, never
 * inside it.
 *
 * ── WHY THIS IS A SECOND CHIP AND NOT A SECOND WORD IN THE FIRST ONE ────────────────
 *
 * It used to be one chip. `TemplateStatusChip` read the rating, composed "Approved · low
 * quality", and — this is the part that did the damage — took its COLOUR from the rating rather
 * than from the verdict. So an approved template rated low rendered ruby: byte for byte the
 * chip a REFUSED template renders. Two opposite facts, one appearance, on a screen where colour
 * is the only signal and there is no legend anywhere.
 *
 * The argument for the old shape was WhatsApp Manager's own cell ("Active – Low Quality"), and
 * it was a good argument for putting the rating next to the verdict. It was not an argument for
 * letting the rating repaint the verdict. Two chips keep the adjacency and lose the collision:
 *
 *     [ Approved ]  [ Low quality ]          approved is blue. Always.
 *
 * ── AND WHY THERE IS STILL NO LEGEND ────────────────────────────────────────────────
 *
 * A legend is a manual, and a manual is what you write when the thing itself cannot be read. So
 * the chip carries its own meaning instead: the word is Meta's own rating, and `note` — the
 * sentence this component exposes for the screens that have room for it — says what the rating
 * DOES, because "medium" is not a thing an owner can act on and "WhatsApp pauses a message that
 * reaches its lowest rating" is.
 *
 * ── WHAT IT REFUSES TO SAY ──────────────────────────────────────────────────────────
 *
 * `UNKNOWN` is Meta's own answer for "no customer feedback yet". It is a real answer and it is
 * not a rating, so it renders as "too early to rate" in slate rather than being folded into a
 * verdict. A row Meta sent no rating for at all renders NOTHING — an absence is not a rating
 * either, and a chip reading "no rating yet" on every row of a list Meta answered without the
 * extra fields is noise that teaches the owner to stop reading the chips.
 */
import { computed } from 'vue';

import { QUALITY_SCORES } from '../helpers/template-facts.js';
import { useWasloI18n } from '../i18n.js';

const props = defineProps({
  /** Meta's `{ score, date }`, or null. There is no `reasons` — see `template-facts.js`. */
  quality: { type: Object, default: null },
  /** The consequence sentence, for the screens with room for it. Off by default. */
  explain: { type: Boolean, default: false },
});

const { t } = useWasloI18n();

/**
 * THE COLOUR IS ABOUT WHAT SHE MUST DO, not about how good the news is — the same rule the
 * verdict chip states next door. A high rating asks nothing of her, so it is slate, exactly like
 * "too early to rate". Giving it a green would also put two near-identical green chips side by
 * side on every approved row, because the brand overlay remaps `n-blue` to emerald and an
 * approved verdict is blue — which is how a screen ends up with colours that mean nothing.
 */
const TONE = {
  HIGH: 'bg-n-slate-3 text-n-slate-11',
  MEDIUM: 'bg-n-amber-3 text-n-amber-11',
  LOW: 'bg-n-ruby-3 text-n-ruby-11',
  UNKNOWN: 'bg-n-slate-3 text-n-slate-11',
};

/** 'HIGH' | 'MEDIUM' | 'LOW' | 'UNKNOWN', or '' when Meta sent no rating at all. */
const rating = computed(() => {
  const score = props.quality?.score;
  if (typeof score !== 'string' || score.trim() === '') return '';
  return QUALITY_SCORES[score.trim().toUpperCase()] ?? '';
});

const tone = computed(() => TONE[rating.value] ?? TONE.UNKNOWN);

const label = computed(() =>
  rating.value ? t(`WASLO.TEMPLATES.QUALITY.${rating.value}`) : ''
);

/** Only where it is worth a line: the two ratings an owner has to do something about. */
const note = computed(() => {
  if (!props.explain) return '';
  if (rating.value !== 'MEDIUM' && rating.value !== 'LOW') return '';
  return t(`WASLO.TEMPLATES.QUALITY_NOTE.${rating.value}`);
});
</script>

<template>
  <span
    v-if="rating"
    class="inline-flex flex-col gap-1 min-w-0"
    :data-waslo-template-quality="rating"
  >
    <span
      class="inline-flex items-center self-start h-6 px-2 text-xs font-medium rounded-md shrink-0"
      :class="tone"
    >
      {{ label }}
    </span>
    <span
      v-if="note"
      class="text-xs leading-5 max-w-[48ch] text-n-slate-11"
      data-waslo-template-quality-note
    >
      {{ note }}
    </span>
  </span>
</template>
